/**
 * Date Input Component
 * ===================
 * 
 * Form input template component for rendering date input fields with validation.
 * This component is designed to be used within individual form components to provide
 * a consistent date input interface with range validation and error handling.
 * 
 * Core Features:
 * - HTML5 date input with custom styling
 * - Date range validation (min/max dates)
 * - Automatic today's date setting capability
 * - Form validation integration with error display
 * - Responsive design with error states
 * - Custom error messages for different validation failures
 * 
 * Input Properties:
 * - templateFormGroup: FormGroup containing the form controls
 * - control: AbstractControl for the specific date field
 * - label: Field label with required indicator
 * - min/max: Date range constraints in ISO format
 * - today: Boolean to automatically set today's date
 * 
 * Validation Features:
 * - Required field validation
 * - Date range validation (min/max)
 * - Custom error messages for validation failures
 * - Touch state management for validation display
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-date-input',
  template: `
    <ng-container *ngIf="templateFormGroup && control" [formGroup]="templateFormGroup">
      <ion-label [position]="position" class="form-label">
        <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>
      <ion-item class="ion-no-padding" lines="none" class="form-input-border">
      <ion-input 
        [id]="controlName"
        #customInput
        [formControl]="formControl"
        type="date"
        [fill]="fill"
        [class]="inputClass"
        [class.ion-invalid]="hasError"
        [class.ion-touched]="control.touched"
        [errorText]="getErrorMessage()"
        [min]="min"
        [max]="max">
      </ion-input>
      </ion-item>
    </ng-container>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }
    

    
    /* Optional: Style invalid inputs */
    ion-input.ion-invalid.ion-touched {
      --border-color: var(--ion-color-danger);
      --color: var(--ion-color-danger);
    }
  `]
})
export class DateInputComponent implements OnChanges, OnInit {
  @Input() templateFormGroup!: FormGroup;
  @Input() control!: AbstractControl | null;
  @Input() label!: string;
  @Input() requiredField: boolean = false;
  @Input() fill: string = 'none';
  @Input() inputClass: string = 'custom-input';
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'floating';
  @Input() today: boolean = false;
  @Input() min: string = '1900-01-01'; // ISO string format, default to 1900
  @Input() max: string = '2100-12-31'; // ISO string format, default to 2100

  controlName: string = '';
  formControl: FormControl = new FormControl();

  /**
   * Computed property to check if control has errors and is touched.
   * Used to determine when to show error states and styling.
   * 
   * @returns True if the control has validation errors and has been touched
   */
  get hasError(): boolean {
    return !!(this.control?.invalid && this.control?.touched);
  }

  /**
   * Gets the appropriate error message based on validation errors.
   * Provides user-friendly error messages for different validation failures.
   * 
   * @returns String containing the appropriate error message
   */
  getErrorMessage(): string {
    if (!this.hasError || !this.control?.errors) {
      return '';
    }

    const errors = this.control.errors;

    if (errors['required']) {
      return `${this.label} is required`;
    }

    if (errors['min']) {
      const minDate = new Date(errors['min'].min);
      return `${this.label} cannot be before ${minDate.toLocaleDateString()}`;
    }

    if (errors['max']) {
      const maxDate = new Date(errors['max'].max);
      return `${this.label} cannot be after ${maxDate.toLocaleDateString()}`;
    }

    if (errors['matDatepickerParse']) {
      return 'Please enter a valid date';
    }

    if (errors['matDatepickerMin']) {
      return 'Date is too early';
    }

    if (errors['matDatepickerMax']) {
      return 'Date is too late';
    }

    // Default error message
    return `${this.label} is invalid`;
  }

  constructor() {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Sets up the control name and initializes the component.
   */
  ngOnInit(): void {
    if (this.control) {
      // Get the control name from the form group
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
    }
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Handles changes to templateFormGroup, control, min/max dates, and today setting.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if ('templateFormGroup' in changes) {
      this.templateFormGroup = changes['templateFormGroup'].currentValue;
    }
    if ('control' in changes && this.control) {
      this.controlName = Object.keys(this.templateFormGroup.controls).find(
        key => this.templateFormGroup.get(key) === this.control
      ) || '';
      this.formControl = this.control as FormControl;
      this.addDateRangeValidation();
    }
    
    // Handle min/max changes
    if (('min' in changes || 'max' in changes) && this.control) {
      this.addDateRangeValidation();
    }
    
    // Handle today input change
    if ('today' in changes && this.control) {
      const todayValue = changes['today'].currentValue;
      if (todayValue === true) {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
        this.control.setValue(todayString);
      }
    }
  }

  /**
   * Adds date range validation to the control.
   * Combines existing validators with custom date range validation.
   * Updates the control's validators and triggers validation.
   * 
   * @private
   */
  private addDateRangeValidation(): void {
    if (!this.control) return;

    const validators = [];
    
    // Add existing validators
    if (this.control.hasValidator(Validators.required)) {
      validators.push(Validators.required);
    }

    // Add custom date range validator
    validators.push(this.dateRangeValidator.bind(this));

    this.control.setValidators(validators);
    this.control.updateValueAndValidity();
  }

  /**
   * Custom validator for date range constraints.
   * Checks if the selected date falls within the min/max date range.
   * 
   * @param control - The form control to validate
   * @returns Validation error object or null if valid
   * @private
   */
  private dateRangeValidator(control: AbstractControl): { [key: string]: any } | null {
    if (!control.value) return null;

    const selectedDate = new Date(control.value);
    const minDate = this.min ? new Date(this.min) : null;
    const maxDate = this.max ? new Date(this.max) : null;

    if (minDate && selectedDate < minDate) {
      return { min: { min: this.min, actual: control.value } };
    }

    if (maxDate && selectedDate > maxDate) {
      return { max: { max: this.max, actual: control.value } };
    }

    return null;
  }
}